import { useState, useEffect, useCallback, useRef } from 'react';
import { Maximize2, Minimize2, Play, Pause, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Section {
    id: string;
    label: string;
    content: React.ReactNode;
}

interface PresentationModeProps {
    sections: Section[];
}

export function PresentationMode({ sections }: PresentationModeProps) {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [activeSection, setActiveSection] = useState(0);
    const [autoPlay, setAutoPlay] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const enterFullscreen = useCallback(async () => {
        try {
            await containerRef.current?.requestFullscreen();
            setIsFullscreen(true);
        } catch {
            // Fallback: just set state for CSS-based fullscreen
            setIsFullscreen(true);
        }
    }, []);

    const exitFullscreen = useCallback(async () => {
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }
        } catch { /* ignore */ }
        setIsFullscreen(false);
        setAutoPlay(false);
    }, []);

    useEffect(() => {
        function handleFullscreenChange() {
            if (!document.fullscreenElement) {
                setIsFullscreen(false);
                setAutoPlay(false);
            }
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Auto-carousel
    useEffect(() => {
        if (autoPlay && isFullscreen) {
            intervalRef.current = setInterval(() => {
                setActiveSection((prev) => (prev + 1) % sections.length);
            }, 15000);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [autoPlay, isFullscreen, sections.length]);

    // Keyboard navigation in fullscreen
    useEffect(() => {
        if (!isFullscreen) return;
        function handleKey(e: KeyboardEvent) {
            if (e.key === 'Escape') { void exitFullscreen(); return; }
            if (e.key === 'ArrowRight' || e.key === ' ') {
                e.preventDefault();
                setActiveSection((prev) => (prev + 1) % sections.length);
            }
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setActiveSection((prev) => (prev - 1 + sections.length) % sections.length);
            }
            if (e.key === 'p' || e.key === 'P') {
                setAutoPlay((prev) => !prev);
            }
        }
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isFullscreen, sections.length, exitFullscreen]);

    if (!isFullscreen) {
        return (
            <Button
                variant='outline'
                size='sm'
                onClick={() => void enterFullscreen()}
                className='gap-2'
            >
                <Maximize2 className='h-4 w-4' />
                Present
            </Button>
        );
    }

    return (
        <div
            ref={containerRef}
            className='fixed inset-0 z-[100] bg-background flex flex-col'
        >
            {/* Top bar with section tabs + controls */}
            <div className='flex items-center justify-between px-4 py-2 border-b border-border bg-card shrink-0'>
                <div className='flex items-center gap-1 overflow-x-auto'>
                    {sections.map((section, idx) => (
                        <button
                            key={section.id}
                            onClick={() => { setActiveSection(idx); setAutoPlay(false); }}
                            className={cn(
                                'px-3 py-1.5 text-sm font-medium rounded transition-colors whitespace-nowrap',
                                idx === activeSection
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                            )}
                        >
                            {section.label}
                        </button>
                    ))}
                </div>

                <div className='flex items-center gap-2 shrink-0 ml-4'>
                    <Button
                        variant='ghost'
                        size='icon'
                        className='h-8 w-8'
                        onClick={() => setActiveSection((prev) => (prev - 1 + sections.length) % sections.length)}
                    >
                        <ChevronLeft className='h-4 w-4' />
                    </Button>
                    <Button
                        variant='ghost'
                        size='icon'
                        className='h-8 w-8'
                        onClick={() => setAutoPlay((prev) => !prev)}
                    >
                        {autoPlay ? <Pause className='h-4 w-4' /> : <Play className='h-4 w-4' />}
                    </Button>
                    <Button
                        variant='ghost'
                        size='icon'
                        className='h-8 w-8'
                        onClick={() => setActiveSection((prev) => (prev + 1) % sections.length)}
                    >
                        <ChevronRight className='h-4 w-4' />
                    </Button>
                    <Button
                        variant='ghost'
                        size='icon'
                        className='h-8 w-8'
                        onClick={() => void exitFullscreen()}
                    >
                        <Minimize2 className='h-4 w-4' />
                    </Button>
                </div>
            </div>

            {/* Section content — fills remaining space */}
            <div className='flex-1 overflow-y-auto p-6'>
                {sections[activeSection]?.content}
            </div>

            {/* Progress indicator */}
            <div className='flex items-center justify-center gap-1.5 py-2 border-t border-border bg-card shrink-0'>
                {sections.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setActiveSection(idx)}
                        className={cn(
                            'w-2 h-2 rounded-full transition-colors',
                            idx === activeSection ? 'bg-primary' : 'bg-muted-foreground/30'
                        )}
                    />
                ))}
                {autoPlay && (
                    <span className='ml-3 text-xs text-muted-foreground'>Auto • 15s</span>
                )}
            </div>
        </div>
    );
}

export function PresentationButton({ sections }: PresentationModeProps) {
    const [active, setActive] = useState(false);

    if (active) {
        return <PresentationModeOverlay sections={sections} onExit={() => setActive(false)} />;
    }

    return (
        <Button
            variant='outline'
            size='sm'
            onClick={() => setActive(true)}
            className='gap-2'
        >
            <Maximize2 className='h-4 w-4' />
            Present
        </Button>
    );
}

function PresentationModeOverlay({ sections, onExit }: PresentationModeProps & { onExit: () => void }) {
    const [activeSection, setActiveSection] = useState(0);
    const [autoPlay, setAutoPlay] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        void containerRef.current?.requestFullscreen?.().catch(() => {});
    }, []);

    useEffect(() => {
        function handleFullscreenChange() {
            if (!document.fullscreenElement) onExit();
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, [onExit]);

    useEffect(() => {
        if (autoPlay) {
            intervalRef.current = setInterval(() => {
                setActiveSection((prev) => (prev + 1) % sections.length);
            }, 15000);
        }
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [autoPlay, sections.length]);

    useEffect(() => {
        function handleKey(e: KeyboardEvent) {
            if (e.key === 'Escape') { onExit(); return; }
            if (e.key === 'ArrowRight' || e.key === ' ') {
                e.preventDefault();
                setActiveSection((prev) => (prev + 1) % sections.length);
            }
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setActiveSection((prev) => (prev - 1 + sections.length) % sections.length);
            }
            if (e.key === 'p' || e.key === 'P') setAutoPlay((prev) => !prev);
        }
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [sections.length, onExit]);

    return (
        <div ref={containerRef} className='fixed inset-0 z-[100] bg-background flex flex-col'>
            <div className='flex items-center justify-between px-4 py-2 border-b border-border bg-card shrink-0'>
                <div className='flex items-center gap-1 overflow-x-auto'>
                    {sections.map((section, idx) => (
                        <button
                            key={section.id}
                            onClick={() => { setActiveSection(idx); setAutoPlay(false); }}
                            className={cn(
                                'px-3 py-1.5 text-sm font-medium rounded transition-colors whitespace-nowrap',
                                idx === activeSection
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                            )}
                        >
                            {section.label}
                        </button>
                    ))}
                </div>
                <div className='flex items-center gap-2 shrink-0 ml-4'>
                    <Button variant='ghost' size='icon' className='h-8 w-8'
                        onClick={() => setActiveSection((prev) => (prev - 1 + sections.length) % sections.length)}>
                        <ChevronLeft className='h-4 w-4' />
                    </Button>
                    <Button variant='ghost' size='icon' className='h-8 w-8'
                        onClick={() => setAutoPlay((prev) => !prev)}>
                        {autoPlay ? <Pause className='h-4 w-4' /> : <Play className='h-4 w-4' />}
                    </Button>
                    <Button variant='ghost' size='icon' className='h-8 w-8'
                        onClick={() => setActiveSection((prev) => (prev + 1) % sections.length)}>
                        <ChevronRight className='h-4 w-4' />
                    </Button>
                    <Button variant='ghost' size='icon' className='h-8 w-8' onClick={onExit}>
                        <Minimize2 className='h-4 w-4' />
                    </Button>
                </div>
            </div>
            <div className='flex-1 overflow-y-auto p-6'>
                {sections[activeSection]?.content}
            </div>
            <div className='flex items-center justify-center gap-1.5 py-2 border-t border-border bg-card shrink-0'>
                {sections.map((_, idx) => (
                    <button key={idx} onClick={() => setActiveSection(idx)}
                        className={cn('w-2 h-2 rounded-full transition-colors',
                            idx === activeSection ? 'bg-primary' : 'bg-muted-foreground/30')} />
                ))}
                {autoPlay && <span className='ml-3 text-xs text-muted-foreground'>Auto • 15s</span>}
            </div>
        </div>
    );
}
